import React, { Fragment } from "react";
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from "@tanstack/react-table";
import EmptyState from "./EmptyState";
import ErrorState, { ErrorBanner } from "./ErrorState";

/**
 * The table every admin list page renders through, so that loading / empty /
 * error are handled once and identically rather than per page.
 *
 * `state` is the whole contract: "idle" | "loading" | "success" | "error".
 * Pages never branch on these themselves.
 *
 * The four cases that matter:
 *   loading, no data yet  → skeleton rows (header stays, so nothing jumps)
 *   loading, data on screen → keep the rows, dim them (a refetch must not blank
 *                             the table the user is reading)
 *   error, no data        → ErrorState: red, Retry
 *   error, data on screen → ErrorBanner above the still-visible rows
 *   success, no rows      → EmptyState: grey, no Retry
 */
function SkeletonRows({ columns, rows = 5 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={`sk-${r}`} className="animate-pulse">
      {columns.map((_c, c) => (
        <td key={`sk-${r}-${c}`} className="px-6 py-4">
          <div className="h-3 rounded bg-slate-200" style={{ width: `${45 + ((r + c) % 4) * 15}%` }} />
        </td>
      ))}
    </tr>
  ));
}

export default function DataTable({
  columns,
  data = [],
  state = "idle",
  error = null,
  onRetry,
  onDismissError,
  getRowId,
  renderExpanded,
  expandedRowId = null,
  onRowClick,
  // empty-state copy
  emptyTitle = "Nothing here",
  emptyMessage,
  emptyIcon,
  isFiltered = false,
  onClearFilters,
  skeletonRows = 5,
}) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    ...(getRowId ? { getRowId } : {}),
  });

  const hasRows = data.length > 0;
  // "idle" counts as loading: the first fetch is queued in an effect, so
  // treating idle as settled would flash "No devices yet" for one paint before
  // the request even starts.
  const isLoading = state === "loading" || state === "idle";
  const isError = state === "error";

  // Hard failure with nothing to show: the table itself is meaningless.
  if (isError && !hasRows) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }

  return (
    <div>
      {isError && hasRows && (
        <ErrorBanner error={error} onRetry={onRetry} onDismiss={onDismissError} />
      )}

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    scope="col"
                    className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody
            className={`divide-y divide-slate-200 bg-white transition-opacity ${
              isLoading && hasRows ? "opacity-60" : ""
            }`}
          >
            {isLoading && !hasRows ? (
              <SkeletonRows columns={columns} rows={skeletonRows} />
            ) : !hasRows ? (
              <tr>
                <td colSpan={columns.length}>
                  <EmptyState
                    icon={emptyIcon}
                    title={emptyTitle}
                    message={emptyMessage}
                    isFiltered={isFiltered}
                    onClearFilters={onClearFilters}
                  />
                </td>
              </tr>
            ) : (
              table.getRowModel().rows.map((row) => (
                // ⚠️ Fragment with a key — the original in ScheduleHistory.jsx
                // used a bare <> and put the key on the inner <tr>, which React
                // does not count as a keyed list item.
                <Fragment key={row.id}>
                  <tr
                    onClick={onRowClick ? () => onRowClick(row.original) : undefined}
                    className={`transition-colors hover:bg-slate-50 ${
                      onRowClick ? "cursor-pointer" : ""
                    }`}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="px-6 py-4 align-top">
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext()
                        )}
                      </td>
                    ))}
                  </tr>

                  {renderExpanded && expandedRowId === row.id && (
                    <tr className="bg-slate-50/70">
                      <td colSpan={columns.length} className="px-6 py-6">
                        {renderExpanded(row.original)}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
